export const ROLE_HIERARCHY = {
  member: 1,
  cell_leader: 2,
  chapter_president: 3,
  comb_chancellor: 4,
  colony_prime_minister: 5,
  overseer: 6,
  master_admin: 7,
};

export const ROLE_NAMES = {
  member: 'Member',
  cell_leader: 'Cell Leader',
  chapter_president: 'Chapter President',
  comb_chancellor: 'Comb Chancellor',
  colony_prime_minister: 'Colony Prime Minister',
  overseer: 'Overseer',
  master_admin: 'Master Admin',
};

export const DEFAULT_PERMISSIONS = {
  member: {
    canSubmitReports: false,
    canApproveReports: false,
    canPromoteMembers: false,
    canViewHierarchy: true,
    canViewOwnReports: true,
    canEditProfile: true,
  },
  cell_leader: {
    canSubmitReports: true,
    canApproveReports: false,
    canPromoteMembers: false,
    canViewHierarchy: true,
    canViewCellReports: true,
    canEditCellDetails: true,
    canManageCellMembers: true,
  },
  chapter_president: {
    canSubmitReports: true,
    canApproveReports: true,
    canPromoteMembers: true,
    canViewHierarchy: true,
    canViewChapterReports: true,
    canEditChapterDetails: true,
    canManageCells: true,
  },
  comb_chancellor: {
    canSubmitReports: true,
    canApproveReports: true,
    canPromoteMembers: true,
    canViewHierarchy: true,
    canViewCombReports: true,
    canEditCombDetails: true,
    canManageChapters: true,
  },
  colony_prime_minister: {
    canSubmitReports: true,
    canApproveReports: true,
    canPromoteMembers: true,
    canViewHierarchy: true,
    canViewColonyReports: true,
    canEditColonyDetails: true,
    canManageCombs: true,
    canGenerateQuarterlyReports: true,
  },
  overseer: {
    canSubmitReports: true,
    canApproveReports: true,
    canPromoteMembers: true,
    canViewHierarchy: true,
    canViewAllReports: true,
    canManageAllUnits: true,
    canGenerateTerritoryReports: true,
  },
  master_admin: {
    isSuperAdmin: true,
    canSubmitReports: true,
    canApproveReports: true,
    canPromoteMembers: true,
    canViewHierarchy: true,
    canViewAllReports: true,
    canManageAllUnits: true,
    canManageUsers: true,
    canManagePromotions: true,
  },
};

export const canUserManageRole = (userRole, targetRole) => {
  return ROLE_HIERARCHY[userRole] > ROLE_HIERARCHY[targetRole];
};

export const canUserAccessData = (userRole, requiredRole) => {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
};

export const getUserPermissions = (role) => {
  return DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.member;
};

export const hasPermission = (permissions, permissionName) => {
  if (permissions?.isSuperAdmin) return true;
  return permissions?.[permissionName] || false;
};

// ===== PROMOTION & HIERARCHY VALIDATION =====

/**
 * Check if a user can promote another user to a specific role
 * Master Admin can promote to any role
 * Other users can only promote to roles below their hierarchy level
 */
export const canUserPromoteToRole = (promoterRole, targetRole) => {
  // Master admin can promote to any role
  if (promoterRole === 'master_admin') {
    return true;
  }

  // Check if promoter has higher hierarchy than target role
  return ROLE_HIERARCHY[promoterRole] > ROLE_HIERARCHY[targetRole];
};

/**
 * Get all roles a user is eligible to promote someone to
 * Master Admin gets all roles except master_admin
 * Others get roles below their level
 */
export const getEligibleRolesForPromotion = (userRole) => {
  const roleOrder = ['member', 'cell_leader', 'chapter_president', 'comb_chancellor', 'colony_prime_minister', 'overseer', 'master_admin'];
  
  if (userRole === 'master_admin') {
    // Master admin can promote to all roles except master_admin
    return roleOrder.filter(role => role !== 'master_admin');
  }

  const userLevel = ROLE_HIERARCHY[userRole];
  
  // Return roles below user's level
  return roleOrder.filter(role => {
    const roleLevel = ROLE_HIERARCHY[role];
    return roleLevel < userLevel;
  });
};

/**
 * Validate promotion eligibility
 * Returns { valid: boolean, message: string }
 */
export const validatePromotion = (promoterRole, targetUserRole, targetPromotionRole) => {
  // Check if promoter can promote
  const permissions = getUserPermissions(promoterRole);
  if (!hasPermission(permissions, 'canPromoteMembers')) {
    return {
      valid: false,
      message: `${ROLE_NAMES[promoterRole]} cannot promote members`
    };
  }

  // Check if promoter can promote to this role
  if (!canUserPromoteToRole(promoterRole, targetPromotionRole)) {
    return {
      valid: false,
      message: `Cannot promote to ${ROLE_NAMES[targetPromotionRole]}. You can only promote to roles below your level.`
    };
  }

  // Check if promoting to same or higher role (shouldn't happen but double-check)
  if (ROLE_HIERARCHY[targetPromotionRole] >= ROLE_HIERARCHY[targetUserRole]) {
    return {
      valid: false,
      message: `Cannot promote to ${ROLE_NAMES[targetPromotionRole]}. Target role must be higher than current role (${ROLE_NAMES[targetUserRole]}).`
    };
  }

  return {
    valid: true,
    message: 'Promotion eligible'
  };
};
